import React, { useEffect, useMemo, useState } from 'react';
import { ResourceField, ResourceFormPage } from '../../components/resource/ResourceFormPage';
import { ItemOrdemProducao, OrdemProducao } from '../../types/producao';
import { ordensProducaoService, catalogoProdutosService, unidadesMedidaService } from '../../services';

interface SelectOption {
  value: number;
  label: string;
}

const createEmptyItem = (): Partial<ItemOrdemProducao> => ({
  produto: undefined,
  quantidade_planejada: undefined,
  quantidade_produzida: undefined,
  tempo_planejado_min: undefined,
  observacoes: '',
  unidade_medida: undefined,
});

const ItensOrdemEditor: React.FC<{
  value: unknown;
  onChange: (items: ItemOrdemProducao[]) => void;
}> = ({ value, onChange }) => {
  const itens = Array.isArray(value) ? (value as Partial<ItemOrdemProducao>[]) : [];
  const [productOptions, setProductOptions] = useState<SelectOption[]>([]);
  const [unitOptions, setUnitOptions] = useState<SelectOption[]>([]);

  useEffect(() => {
    catalogoProdutosService
      .list({ page_size: 200 })
      .then((res) =>
        setProductOptions(
          res.results.map((item: any) => ({
            value: item.id,
            label: `${item.codigo ?? ''} ${item.descricao ?? ''}`.trim() || String(item.id),
          })),
        ),
      )
      .catch((err) => console.error('Erro ao carregar produtos do catálogo', err));
    unidadesMedidaService
      .list({ page_size: 200 })
      .then((res) =>
        setUnitOptions(
          res.results.map((item: any) => ({
            value: item.id,
            label: `${item.codigo ?? ''} ${item.descricao ?? ''}`.trim() || String(item.id),
          })),
        ),
      )
      .catch((err) => console.error('Erro ao carregar unidades de medida', err));
  }, []);

  const handleUpdate = (index: number, patch: Partial<ItemOrdemProducao>) => {
    const next = itens.map((item, idx) => (idx === index ? { ...item, ...patch } : item));
    onChange(next as ItemOrdemProducao[]);
  };

  const handleAdd = () => {
    onChange([...itens, createEmptyItem()] as ItemOrdemProducao[]);
  };

  const handleRemove = (index: number) => {
    onChange(itens.filter((_, idx) => idx !== index) as ItemOrdemProducao[]);
  };

  const formatNumberValue = (value?: number | null) => (value === undefined || value === null ? '' : String(value));

  const productOptionsWithItems = useMemo(() => {
    const byValue = new Map<number, SelectOption>();
    productOptions.forEach((option) => {
      byValue.set(option.value, option);
    });
    itens.forEach((item) => {
      if (item.produto && !byValue.has(item.produto)) {
        byValue.set(item.produto, {
          value: item.produto,
          label: item.produto_nome ?? `#${item.produto}`,
        });
      }
    });
    return Array.from(byValue.values());
  }, [productOptions, itens]);

  return (
    <div className="border rounded-3 bg-white shadow-sm p-3">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div>
          <strong>Itens</strong>
          <div className="text-muted small">Adicione, remova ou ajuste os produtos previstos para a ordem.</div>
        </div>
        <button type="button" className="btn btn-outline-primary btn-sm" onClick={handleAdd}>
          Adicionar item
        </button>
      </div>

      {itens.length === 0 && <p className="text-muted mb-0">Nenhum item informado.</p>}

      {itens.map((item, index) => (
        <div key={item.id ?? index} className="border rounded p-3 mb-3">
          <div className="row g-2 align-items-end">
            <div className="col-12 col-md-5">
              <label className="form-label">Produto</label>
              <select
                className="form-select"
                value={item.produto ?? ''}
                onChange={(event) =>
                  handleUpdate(index, {
                    produto: event.target.value === '' ? undefined : Number(event.target.value),
                  })
                }
                required
              >
                <option value="">Selecione</option>
                {productOptionsWithItems.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-6 col-md-2">
              <label className="form-label">Planejada</label>
              <input
                type="number"
                className="form-control"
                value={formatNumberValue(item.quantidade_planejada)}
                onChange={(event) =>
                  handleUpdate(index, {
                    quantidade_planejada:
                      event.target.value === '' ? undefined : Number(event.target.value),
                  })
                }
                required
              />
            </div>
            <div className="col-12 col-md-3">
              <label className="form-label">Unidade de medida</label>
              <select
                className="form-select"
                value={item.unidade_medida ?? ''}
                onChange={(event) =>
                  handleUpdate(index, {
                    unidade_medida: event.target.value === '' ? undefined : Number(event.target.value),
                  })
                }
              >
                <option value="">Selecione</option>
                {unitOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-12 col-md-1 d-flex justify-content-end">
              <button
                type="button"
                className="btn btn-outline-danger btn-sm w-100 w-md-auto"
                onClick={() => handleRemove(index)}
                aria-label="Remover item"
              >
                Remover
              </button>
            </div>
            <div className="col-12">
              <label className="form-label">Observações</label>
              <textarea
                className="form-control"
                rows={2}
                value={item.observacoes ?? ''}
                onChange={(event) => handleUpdate(index, { observacoes: event.target.value })}
              />
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

const fields: ResourceField<OrdemProducao>[] = [
  { name: 'codigo', label: 'Código', required: true },
  { name: 'data_inicio_prevista', label: 'Início previsto', type: 'date', required: true },
  { name: 'data_fim_prevista', label: 'Fim previsto', type: 'date', required: true },
  {
    name: 'status',
    label: 'Status',
    type: 'select',
    options: [
      { label: 'Planejada', value: 'PLANEJADA' },
      { label: 'Em andamento', value: 'EM_ANDAMENTO' },
      { label: 'Concluída', value: 'CONCLUIDA' },
      { label: 'Cancelada', value: 'CANCELADA' },
    ],
    required: true,
  },
  {
    name: 'itens',
    label: 'Itens da ordem',
    render: ({ value, onChange }) => <ItensOrdemEditor value={value} onChange={(items) => onChange(items)} />,
  },
];

export const OrdensProducaoFormPage: React.FC = () => (
  <ResourceFormPage
    title="Nova ordem de produção"
    description="Cadastre uma ordem de produção."
    fields={fields}
    service={ordensProducaoService}
    redirectPath="/admin/producao/ordens-producao"
    cancelPath="/admin/producao/ordens-producao"
  />
);
